import { useCallback } from "react";

/**
 * Error thrown for non-2xx API responses. Carries the HTTP status and the raw
 * `detail` payload so callers can branch on things like a 402 plan-limit block
 * (detail = { code: "plan_limit_reached", limit, plan, upgrade_url }).
 */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: unknown;

  constructor(message: string, status: number, detail: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

/** Parse a failed Response and throw a rich ApiError. */
export async function throwApiError(response: Response): Promise<never> {
  const body = (await response.json().catch(() => ({}))) as {
    detail?: unknown;
  };
  const detail = body.detail;

  let message = "Request failed";
  if (typeof detail === "string") {
    message = detail;
  } else if (detail && typeof detail === "object") {
    const d = detail as Record<string, unknown>;
    if (typeof d.message === "string") {
      message = d.message;
    } else if (typeof d.code === "string") {
      message = d.code;
    }
  }

  throw new ApiError(message, response.status, detail);
}

/**
 * Hook for making authenticated requests to the backend API.
 *
 * Each request explicitly attaches a Shopify session token obtained from the
 * App Bridge CDN global (`shopify.idToken()`) as an `Authorization: Bearer`
 * header. We do NOT rely on App Bridge implicitly patching `fetch` — that was
 * not attaching the header in the embedded iframe, causing every authenticated
 * call to 401.
 *
 * @see https://shopify.dev/docs/api/app-bridge-library/reference/id-token
 * @see https://shopify.dev/docs/apps/build/authentication-authorization/session-tokens
 */
export function useShopifyFetch() {
  const fetchWithAuth = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      // Ensure we're calling our backend API
      const apiUrl = url.startsWith("/") ? url : `/${url}`;

      const headers: Record<string, string> = {
        "Content-Type": "application/json",
        ...(options.headers as Record<string, string>),
      };

      // Attach the App Bridge session token explicitly. The global `shopify`
      // object is provided by the App Bridge CDN script (app-bridge.js) loaded
      // in index.html; `shopify.idToken()` returns a fresh, signed session
      // token. Relying on App Bridge to implicitly patch `fetch` proved
      // unreliable in the embedded iframe (requests went out with no
      // Authorization header → the backend's get_current_shop returned 401 and
      // the dashboard showed "Failed to load shop information"), so we set the
      // header ourselves.
      try {
        const bridge = (
          window as unknown as { shopify?: { idToken?: () => Promise<string> } }
        ).shopify;
        if (bridge && typeof bridge.idToken === "function") {
          const token = await bridge.idToken();
          if (token) {
            headers["Authorization"] = `Bearer ${token}`;
          }
        }
      } catch {
        // If the token can't be obtained the request will 401 and the caller
        // surfaces the error — preferable to silently sending no auth.
      }

      const response = await fetch(apiUrl, {
        ...options,
        headers,
      });

      return response;
    },
    [],
  );

  const get = useCallback(
    async <T>(url: string): Promise<T> => {
      const response = await fetchWithAuth(url);
      if (!response.ok) {
        await throwApiError(response);
      }
      return response.json();
    },
    [fetchWithAuth],
  );

  const post = useCallback(
    async <T>(url: string, data?: unknown): Promise<T> => {
      const response = await fetchWithAuth(url, {
        method: "POST",
        body: data ? JSON.stringify(data) : undefined,
      });
      if (!response.ok) {
        await throwApiError(response);
      }
      return response.json();
    },
    [fetchWithAuth],
  );

  const put = useCallback(
    async <T>(url: string, data?: unknown): Promise<T> => {
      const response = await fetchWithAuth(url, {
        method: "PUT",
        body: data ? JSON.stringify(data) : undefined,
      });
      if (!response.ok) {
        await throwApiError(response);
      }
      return response.json();
    },
    [fetchWithAuth],
  );

  const del = useCallback(
    async <T>(url: string): Promise<T> => {
      const response = await fetchWithAuth(url, {
        method: "DELETE",
      });
      if (!response.ok) {
        await throwApiError(response);
      }
      return response.json();
    },
    [fetchWithAuth],
  );

  return { fetch: fetchWithAuth, get, post, put, del };
}

// Type definitions for API responses
export interface ShopInfo {
  shop_domain: string;
  shop_name: string;
  is_active: boolean;
  installed_at: string | null;
  brand_id: string | null;
  is_connected: boolean;
  settings: {
    auto_sync_products: boolean;
    cookie_duration_days: number;
    attribution_model: string;
  };
}

export interface Product {
  id: string;
  shopify_product_id: number;
  title: string;
  handle: string;
  price: number | null;
  compare_at_price: number | null;
  image_url: string | null;
  is_synced: boolean;
  last_synced_at: string | null;
  [key: string]: unknown; // Index signature for compatibility
}

export interface ProductsResponse {
  products: Product[];
  total: number;
  limit: number;
  offset: number;
}

export interface AnalyticsOverview {
  conversions: number;
  revenue: number;
  clicks: number;
  top_affiliates: Array<{
    id: string;
    name: string;
    conversions: number;
    revenue: number;
  }>;
  top_products: Array<{
    id: string;
    title: string;
    conversions: number;
    revenue: number;
  }>;
}

export interface GeneratedLink {
  link_id: string;
  affiliate_url: string;
  tracking_code: string;
  short_url: string;
  qr_code_url: string;
  product?: {
    id: string;
    title: string;
    original_url: string;
  };
}

export interface LinksResponse {
  links: GeneratedLink[];
  total: number;
}

export interface ConversionData {
  conversions: number;
  revenue: number;
  clicks: number;
  period?: string;
}
